import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Link,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  CheckCircleOutline,
  HourglassEmpty,
  Insights,
  PersonOutline,
  Refresh as RefreshIcon,
  SmartToy,
  TrendingUp,
} from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { addDays, format } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import PageHeader from './PageHeader';
import { getVisionReviewStats } from '../utils/api';

const CHART_SERIES = [
  { key: 'aiMatched', paramKey: 'ai_matched', label: 'AI Matched', color: '#10b981' },
  { key: 'aiNotMatched', paramKey: 'ai_not_matched', label: 'AI Not Matched', color: '#ef4444' },
  { key: 'pendingReview', paramKey: 'pending_review', label: 'Pending Review', color: '#f59e0b' },
  { key: 'manuallyResolved', paramKey: 'manually_resolved', label: 'Manually Resolved', color: '#6366f1' },
];

const CHART_PANEL_SX = {
  border: '1px solid rgba(64, 81, 138, 0.12)',
  borderRadius: 2.5,
  p: { xs: 1.5, sm: 2, md: 2.25 },
  bgcolor: '#ffffff',
  background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, #ffffff 55%)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
};

const CHART_FOCUS_SX = {
  '& .recharts-wrapper:focus, & .recharts-surface:focus, & .recharts-rectangle:focus': {
    outline: 'none',
  },
};

const ChartLegend = ({ items }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    useFlexGap
    sx={{ gap: 1, mb: 1.5 }}
  >
    {items.map(({ label, color }) => (
      <Stack
        key={label}
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{
          px: 1.1,
          py: 0.45,
          borderRadius: 999,
          border: '1px solid rgba(64, 81, 138, 0.1)',
          bgcolor: 'rgba(255, 255, 255, 0.9)',
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 0 2px ${color}22` }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

const OutcomesTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload || {};
  const items = [
    { label: 'AI Matched', value: row.aiMatched, color: '#10b981' },
    { label: 'AI Not Matched', value: row.aiNotMatched, color: '#ef4444' },
    { label: 'Pending Review', value: row.pendingReview, color: '#f59e0b' },
    { label: 'Manually Resolved', value: row.manuallyResolved, color: '#6366f1' },
  ];

  return (
    <Box
      sx={{
        minWidth: 210,
        px: 1.75,
        py: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(64, 81, 138, 0.14)',
        bgcolor: 'rgba(255, 255, 255, 0.98)',
        boxShadow: '0 12px 32px rgba(31, 45, 84, 0.14)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#40518A', mb: 1 }}>
        {label ? format(new Date(label), 'EEEE, MMM dd') : '—'}
      </Typography>
      <Stack spacing={0.75}>
        {items.map(({ label: itemLabel, value, color }) => (
          <Stack key={itemLabel} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
              <Typography variant="caption" color="text.secondary">
                {itemLabel}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#111827' }}>
              {Number(value || 0).toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ mt: 1.1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Total possible matches
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#40518A' }}>
            {Number(row.totalPossible || 0).toLocaleString()}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

const SuccessRateTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const value = payload[0]?.value;
  const row = payload[0]?.payload || {};

  return (
    <Box
      sx={{
        minWidth: 180,
        px: 1.75,
        py: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(14, 165, 233, 0.18)',
        bgcolor: 'rgba(255, 255, 255, 0.98)',
        boxShadow: '0 12px 32px rgba(31, 45, 84, 0.14)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#40518A', mb: 0.75 }}>
        {label ? format(new Date(label), 'EEEE, MMM dd') : '—'}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0ea5e9', lineHeight: 1.2 }}>
        {value == null ? '—' : `${value}%`}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {Number(row.aiMatched || 0).toLocaleString()} matched · {Number(row.aiNotMatched || 0).toLocaleString()} not matched
      </Typography>
    </Box>
  );
};

const METRIC_TILES = [
  {
    key: 'total_possible_matches',
    label: 'Total Possible Matches',
    color: '#40518A',
    bg: 'rgba(64, 81, 138, 0.08)',
    icon: Insights,
  },
  {
    key: 'ai_matched',
    label: 'AI Matched',
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.1)',
    icon: CheckCircleOutline,
  },
  {
    key: 'ai_not_matched',
    label: 'AI Not Matched',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.1)',
    icon: SmartToy,
  },
  {
    key: 'pending_review',
    label: 'Pending Review',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.12)',
    icon: HourglassEmpty,
  },
  {
    key: 'manually_resolved',
    label: 'Manually Resolved',
    color: '#6366f1',
    bg: 'rgba(99, 102, 241, 0.1)',
    icon: PersonOutline,
  },
  {
    key: 'success_rate',
    label: 'AI Match Success Rate',
    color: '#0ea5e9',
    bg: 'rgba(14, 165, 233, 0.1)',
    icon: TrendingUp,
    isRate: true,
  },
];

const createDefaultRange = () => ([
  {
    startDate: addDays(new Date(), -7),
    endDate: new Date(),
    key: 'selection',
  },
]);

const METRIC_RESULT_KEYS = {
  total_possible_matches: 'total_possible_matches',
  ai_matched: 'ai_matched',
  ai_not_matched: 'ai_not_matched',
  pending_review: 'pending_review',
  manually_resolved: 'manually_resolved',
};

const CLICKABLE_TILE_SX = {
  cursor: 'pointer',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 18px rgba(31, 45, 84, 0.12)',
  },
};

const VisionReviewStatistics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const today = new Date();
  const [dateRange, setDateRange] = useState(createDefaultRange);
  const [draftDateRange, setDraftDateRange] = useState(createDefaultRange);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const buildParams = useCallback(() => ({
    start_date: format(dateRange[0].startDate, 'yyyy-MM-dd'),
    end_date: format(dateRange[0].endDate, 'yyyy-MM-dd'),
  }), [dateRange]);

  const fetchStats = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const data = await getVisionReviewStats(buildParams());
      setStats(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch AI Matching stats:', err);
      setError(err.message || 'Unable to load AI Matching statistics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const chartData = useMemo(() => {
    if (!stats?.daily_counts?.length) return [];
    return stats.daily_counts.map((entry) => {
      const reviewed = (entry.ai_matched || 0) + (entry.ai_not_matched || 0);
      const successRate = reviewed > 0
        ? Math.round(((entry.ai_matched || 0) / reviewed) * 1000) / 10
        : null;
      return {
        date: entry.date,
        aiMatched: entry.ai_matched || 0,
        aiNotMatched: entry.ai_not_matched || 0,
        pendingReview: entry.pending_review || 0,
        manuallyResolved: entry.manually_resolved || 0,
        totalPossible: entry.total_possible_matches || 0,
        successRate,
      };
    });
  }, [stats]);

  const hasChartData = chartData.some(
    (day) => day.aiMatched || day.aiNotMatched || day.pendingReview || day.manuallyResolved
  );

  const formatMetricValue = (key, totals) => {
    if (key === 'success_rate') {
      const value = Number(totals?.success_rate || 0);
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    }
    return Number(totals?.[key] || 0).toLocaleString();
  };

  const handleOpenCalendar = (event) => {
    setDraftDateRange(dateRange);
    setAnchorEl(event.currentTarget);
    setCalendarOpen(true);
  };

  const handleCloseCalendar = () => {
    setCalendarOpen(false);
  };

  const handleApplyDateRange = () => {
    setDateRange(draftDateRange);
    handleCloseCalendar();
  };

  const navigateToLogs = useCallback(({ resultKey, chartDate } = {}) => {
    const params = new URLSearchParams();
    const startDate = chartDate || format(dateRange[0].startDate, 'yyyy-MM-dd');
    const endDate = chartDate || format(dateRange[0].endDate, 'yyyy-MM-dd');
    params.set('start_date', startDate);
    params.set('end_date', endDate);
    if (resultKey && resultKey !== 'success_rate') {
      params.set('vision_review_result', resultKey);
    }
    navigate(`/in-out?${params.toString()}`);
  }, [dateRange, navigate]);

  const handleMetricTileClick = useCallback((metricKey) => {
    const resultKey = METRIC_RESULT_KEYS[metricKey];
    if (!resultKey) return;
    navigateToLogs({ resultKey });
  }, [navigateToLogs]);

  const handleChartSegmentClick = useCallback((paramKey, chartEntry) => {
    if (!paramKey || !chartEntry?.date) return;
    navigateToLogs({ resultKey: paramKey, chartDate: chartEntry.date });
  }, [navigateToLogs]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <PageHeader
          title="AI Matching Statistics"
          subtitle="Track possible-match volume, AI outcomes, and manual resolutions over time."
        />
        <Button
          component={RouterLink}
          to="/ai-review"
          variant="outlined"
          size="small"
          startIcon={<ArrowBack />}
          fullWidth={isMobile}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap', borderRadius: 1.5 }}
        >
          Settings
        </Button>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Daily AI Matching Report
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {lastUpdated
                ? `Last updated ${format(lastUpdated, 'MMM dd, yyyy HH:mm')} · use Refresh to reload`
                : 'Use Refresh to reload statistics'}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={handleOpenCalendar}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenCalendar(event);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 2,
                py: 1,
                cursor: 'pointer',
                bgcolor: 'background.paper',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {format(dateRange[0].startDate, 'MMM dd, yyyy')} - {format(dateRange[0].endDate, 'MMM dd, yyyy')}
              </Typography>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenCalendar(event);
                }}
                aria-label="Choose date range"
              >
                <CalendarToday fontSize="small" />
              </IconButton>
            </Box>
            <Button
              variant="outlined"
              onClick={() => fetchStats({ silent: true })}
              disabled={loading || refreshing}
              startIcon={
                loading || refreshing
                  ? <CircularProgress size={20} color="inherit" />
                  : <RefreshIcon />
              }
              fullWidth={isMobile}
              sx={{ borderRadius: 2 }}
            >
              {loading || refreshing ? 'Loading...' : 'Refresh'}
            </Button>
       
          </Stack>
        </Box>

        <Popover
          open={calendarOpen}
          anchorEl={anchorEl}
          onClose={handleCloseCalendar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 0 }}>
            <DateRangePicker
              ranges={draftDateRange}
              onChange={(ranges) => setDraftDateRange([ranges.selection])}
              moveRangeOnFirstSelection={false}
              months={1}
              direction="horizontal"
              rangeColors={['#40518A']}
              staticRanges={[]}
              inputRanges={[]}
              maxDate={today}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
              <Button onClick={handleCloseCalendar} variant="outlined" size="small">
                Cancel
              </Button>
              <Button onClick={handleApplyDateRange} variant="contained" size="small" color="primary">
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {!loading && !error && stats && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(6, minmax(0, 1fr))',
                  },
                  gap: 1.25,
                  mb: 2.5,
                }}
              >
                {METRIC_TILES.map(({ key, label, color, bg, icon: Icon, isRate }) => (
                  <Box
                    key={key}
                    role={METRIC_RESULT_KEYS[key] ? 'button' : undefined}
                    tabIndex={METRIC_RESULT_KEYS[key] ? 0 : undefined}
                    onClick={METRIC_RESULT_KEYS[key] ? () => handleMetricTileClick(key) : undefined}
                    onKeyDown={METRIC_RESULT_KEYS[key] ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleMetricTileClick(key);
                      }
                    } : undefined}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 1.25,
                      py: 1.1,
                      bgcolor: bg,
                      minHeight: 92,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      ...(METRIC_RESULT_KEYS[key] ? CLICKABLE_TILE_SX : {}),
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Icon sx={{ fontSize: 18, color }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                        {label}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color,
                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                        mt: 0.75,
                      }}
                    >
                      {formatMetricValue(key, stats.totals)}
                    </Typography>
                    {isRate && (
                      <Typography variant="caption" color="text.secondary">
                        Of AI-reviewed possible matches
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {hasChartData ? (
                <Stack spacing={2}>
                  <Box sx={CHART_PANEL_SX}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#40518A' }}>
                          Daily outcomes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Stacked breakdown of how possible matches were resolved each day. Click a segment or metric tile to open matching logs.
                        </Typography>
                      </Box>
                    </Stack>
                    <ChartLegend items={CHART_SERIES} />
                    <Box sx={{ ...CHART_FOCUS_SX, width: '100%', minHeight: { xs: 300, sm: 360 } }}>
                      <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                          data={chartData}
                          margin={{ top: 12, right: 12, left: 4, bottom: 4 }}
                          barCategoryGap="22%"
                        >
                          <CartesianGrid strokeDasharray="4 6" vertical={false} stroke="rgba(64, 81, 138, 0.12)" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                            tick={{ fontSize: 11, fill: '#667085' }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                            minTickGap={24}
                            dy={8}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: '#667085' }}
                            axisLine={false}
                            tickLine={false}
                            width={36}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(64, 81, 138, 0.06)' }}
                            content={<OutcomesTooltip />}
                          />
                          <Bar
                            dataKey="aiMatched"
                            stackId="outcomes"
                            fill="#10b981"
                            radius={[0, 0, 0, 0]}
                            maxBarSize={42}
                            style={{ cursor: 'pointer' }}
                            onClick={(entry) => handleChartSegmentClick('ai_matched', entry)}
                          />
                          <Bar
                            dataKey="aiNotMatched"
                            stackId="outcomes"
                            fill="#ef4444"
                            radius={[0, 0, 0, 0]}
                            maxBarSize={42}
                            style={{ cursor: 'pointer' }}
                            onClick={(entry) => handleChartSegmentClick('ai_not_matched', entry)}
                          />
                          <Bar
                            dataKey="pendingReview"
                            stackId="outcomes"
                            fill="#f59e0b"
                            radius={[0, 0, 0, 0]}
                            maxBarSize={42}
                            style={{ cursor: 'pointer' }}
                            onClick={(entry) => handleChartSegmentClick('pending_review', entry)}
                          />
                          <Bar
                            dataKey="manuallyResolved"
                            stackId="outcomes"
                            fill="#6366f1"
                            radius={[8, 8, 0, 0]}
                            maxBarSize={42}
                            style={{ cursor: 'pointer' }}
                            onClick={(entry) => handleChartSegmentClick('manually_resolved', entry)}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  <Box sx={CHART_PANEL_SX}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#40518A' }}>
                          AI match success rate
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Percentage of AI-reviewed matches that found a candidate
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#0ea5e9' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          Success rate
                        </Typography>
                      </Stack>
                    </Stack>
                    <Box sx={{ ...CHART_FOCUS_SX, width: '100%', minHeight: { xs: 260, sm: 300 } }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData} margin={{ top: 12, right: 12, left: 4, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="4 6" vertical={false} stroke="rgba(64, 81, 138, 0.12)" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                            tick={{ fontSize: 11, fill: '#667085' }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                            minTickGap={24}
                            dy={8}
                          />
                          <YAxis
                            domain={[0, 100]}
                            tickFormatter={(value) => `${value}%`}
                            tick={{ fontSize: 11, fill: '#667085' }}
                            axisLine={false}
                            tickLine={false}
                            width={42}
                          />
                          <Tooltip content={<SuccessRateTooltip />} />
                          <Line
                            type="linear"
                            dataKey="successRate"
                            name="Success Rate"
                            stroke="#0ea5e9"
                            strokeWidth={2.5}
                            connectNulls
                            dot={{
                              r: 5,
                              fill: '#0ea5e9',
                              stroke: '#ffffff',
                              strokeWidth: 2,
                            }}
                            activeDot={{
                              r: 7,
                              fill: '#0284c7',
                              stroke: '#ffffff',
                              strokeWidth: 2,
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    height: 320,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.default',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No AI Matching data available for the selected period.
                  </Typography>
                  <Link component={RouterLink} to="/in-out" underline="hover" sx={{ fontWeight: 600 }}>
                    Open Logs to run AI Matching
                  </Link>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VisionReviewStatistics;
